import { FC, MouseEvent, useState } from "react";
import {
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip
} from "@mui/material";
import { Check, MoreVert } from "@mui/icons-material";
import { getStatus, Progress } from "../departmentProgress/utils";
import { ProgressOverride } from "./buildProgressOverride";

export type { ProgressOverride } from "./buildProgressOverride";
export { buildProgressOverride } from "./buildProgressOverride";

const OPTIONS: { value: Progress; label: string }[] = [
  { value: Progress.NotStarted, label: "Not started" },
  { value: Progress.InProgress, label: "In progress" },
  { value: Progress.Completed, label: "Completed" }
];

type Props = {
  override: ProgressOverride;
};

/**
 * Deliberately tucked behind a small kebab button in the dialog title: a correction tool for
 * Admin and Market Access, not part of the normal Save / Complete flow. The dialog decides who sees it.
 */
export const ProgressOverrideMenu: FC<Props> = ({ override }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = getStatus(override.value);

  const handleOpen = (event: MouseEvent<HTMLElement>) =>
    setAnchor(event.currentTarget);
  const handleClose = () => setAnchor(null);

  const handleSelect = (progress: Progress) => {
    handleClose();
    override.onChange(progress);
  };

  return (
    <>
      <Tooltip title="Set progress">
        <span>
          <IconButton
            size="small"
            onClick={handleOpen}
            disabled={override.isSaving}
            aria-label="Set lifecycle progress"
            sx={{ color: "text.disabled" }}
          >
            {override.isSaving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <MoreVert fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <ListSubheader sx={{ lineHeight: "36px" }}>Set progress</ListSubheader>
        {OPTIONS.map((option) => {
          const selected = getStatus(option.value) === current;
          return (
            <MenuItem
              key={option.value}
              selected={selected}
              disabled={selected}
              onClick={() => handleSelect(option.value)}
            >
              <ListItemIcon>
                {selected && <Check fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
